/** « Leave a review » (D-142, action 5): after a finished mock exam, a mock oral with a trainer
 * or an official result, the learner may leave a rating and a text, with an explicit consent to
 * publication (first name + initial + country). Published only after the team has read it; the
 * learner sees its status and may withdraw it. */

import {
  ApiError,
  getAcademyMeReviewsRetrieveQueryKey,
  useAcademyMeReviewsCreate,
  useAcademyMeReviewsRetrieve,
  useAcademyMeReviewsWithdrawCreate,
} from "@agencyos/api-client";
import { stars } from "@agencyos/core";
import { Badge, Button, Checkbox, GlassCard, TextAreaField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

interface Occasion {
  source: string;
  ref: string;
  exam: string;
  at: string;
}

export function ReviewPrompt({ source }: { source?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mine = useAcademyMeReviewsRetrieve({ query: { retry: false } });
  const data = mine.data?.status === 200 ? mine.data.data : undefined;
  const create = useAcademyMeReviewsCreate();
  const withdraw = useAcademyMeReviewsWithdrawCreate();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const occasions = ((Array.isArray(data?.occasions) ? data?.occasions : []) as Occasion[]).filter((o) => !source || o.source === source);
  const occasion = occasions[0];
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getAcademyMeReviewsRetrieveQueryKey() });
  const reviews = data?.reviews ?? [];
  if (!data?.enabled || (!occasion && !reviews.length && !sent)) return null;

  const onSend = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!occasion) return;
    if (!consent) return setError(t("coaching.reviews.consentRequired"));
    create.mutate(
      { data: { source: occasion.source as never, ref: occasion.ref, rating, text: text.trim(), consent: true } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          setSent(true);
          setText("");
          refresh();
        },
        onError: (failure) => {
          const body = failure instanceof ApiError ? (failure.body as Record<string, unknown> | undefined) : undefined;
          const first = body ? Object.values(body).flat()[0] : undefined;
          setError(typeof first === "string" ? first : t("common.error"));
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="co-review" aria-labelledby="co-review-title">
      <h2 id="co-review-title" className="ac-title-3">
        <Star aria-hidden size={16} /> {t("coaching.reviews.title")}
      </h2>
      {sent ? (
        <p role="status" className="notice-success">
          {t("coaching.reviews.sent")}
        </p>
      ) : null}
      {occasion ? (
        <form className="ac-form" onSubmit={onSend} noValidate>
          <p className="ac-footnote">{t(`coaching.reviews.occasion.${occasion.source}`)}</p>
          <p className="ac-footnote">{t("coaching.reviews.intro")}</p>
          <fieldset className="co-stars">
            <legend>{t("coaching.reviews.rating")}</legend>
            {[1, 2, 3, 4, 5].map((n) => (
              <label key={n} className={`co-star${n <= rating ? " co-star--on" : ""}`}>
                <input type="radio" name="rating" value={n} checked={rating === n} onChange={() => setRating(n)} />
                <span aria-hidden="true">★</span>
                <span className="visually-hidden">{t("coaching.reviews.ratingValue", { count: n })}</span>
              </label>
            ))}
          </fieldset>
          <TextAreaField
            label={t("coaching.reviews.text")}
            rows={4}
            maxLength={data.max_chars}
            value={text}
            hint={t("coaching.reviews.chars", { count: text.trim().length, max: data.max_chars, min: data.min_chars })}
            onChange={(event) => setText(event.target.value)}
          />
          <Checkbox label={t("coaching.reviews.consent", { name: data.display_name })} checked={consent} onChange={(event) => setConsent(event.target.checked)} />
          {error ? (
            <p className="ko" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={create.isPending || text.trim().length < data.min_chars}>
            {t("coaching.reviews.send")}
          </Button>
        </form>
      ) : null}
      {reviews.length ? (
        <div className="co-review__mine">
          <h3 className="ac-title-3">{t("coaching.reviews.mine")}</h3>
          <ul className="plain-list">
            {reviews.map((r) => (
              <li key={r.id} className="co-review__row">
                <span aria-label={t("coaching.reviews.ratingValue", { count: r.rating })}>{stars(r.rating)}</span>
                <Badge>{t(`coaching.reviews.status.${r.status}`)}</Badge>
                <span className="ac-footnote">« {r.text} »</span>
                {r.status !== "withdrawn" ? (
                  <Button variant="ghost" disabled={withdraw.isPending} onClick={() => withdraw.mutate({ id: r.id }, { onSuccess: refresh })}>
                    {t("coaching.reviews.withdraw")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </GlassCard>
  );
}
